import { DocumentNode, gql } from '@apollo/client';
import { filterNotEmpty } from 'utils/data/listHelpers';
import { print } from 'graphql';
import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { OperationDefinitionNode } from 'graphql/language/ast';

/*
 * It takes single mutation and creates batch mutation thanks to gql aliases.
 * See spec file for more details.
 */

export interface BulkMutationFactoryOutput<T = any> {
  query: string;
  apolloQuery: DocumentNode;
  payload: { [prop: string]: T };
}

/**
 * @deprecated use bulkApolloMutationFactory instead - it supports fragments in more ways - nesting, multiple fragments etc.
 */
export function bulkMutationFactory<T>(
  query: string | DocumentNode | TypedDocumentNode,
  data: T[]
): BulkMutationFactoryOutput<T> {
  if (typeof query !== 'string') {
    return bulkApolloMutationFactory(query, data as any) as BulkMutationFactoryOutput<T>;
  }
  let queryString = query;
  if (queryString === null) {
    throw new Error(`Couldn't parse this query`);
  }

  if (queryString.includes('fragment')) {
    queryString = replaceFragments(queryString);
  }

  const content = filterNotEmpty(
    queryString
      .split('\n')
      .filter((queryContent) => !!queryContent)
      .map((queryContent) => queryContent.trim()),
    { removeEmptyStrings: true }
  );

  const operationLines = content.slice(1, content.length - 1);
  const aliases = createAliasesAndPayload(operationLines, data);

  content[0] = replaceArguments(content[0] ?? '', data);
  content.splice(1, content.length - 2, ...aliases.queries);
  return {
    query: content.join('\n'),
    apolloQuery: gql(content.join('\n')),
    payload: aliases.payload
  };
}

function replaceArguments(mutationLine: string, data: any[]): string {
  const { match, args } = getArguments(mutationLine);
  const argsReplacement = data.map((d, i) => args.map((type) => `${type[0]}${`${i}`}: ${type[1]}`)).join(', ');
  return mutationLine.replace(match[1] ?? '', argsReplacement);
}

/*
 * It takes mutation, duplicates that mutation and creates aliases based on provided variables.
 * Number of variables equals number of aliases.
 * Aliases like "person0: patchPerson(person: $person0) { ... }" are the only way to do batching.
 */
function createAliasesAndPayload<T>(
  methodLines: string[],
  data: T[]
): { queries: string[]; payload: { [prop: string]: T } } {
  const duplicated: string[] = [];
  const payload: { [prop: string]: T } = {};
  const { match, args } = getArguments(methodLines[0] ?? '');

  data.forEach((d, i) => {
    if (args.length > 1) {
      args.forEach((type) => {
        payload[`${type[0]}${`${i}`}`] = (d as Record<string, any>)[type[0]];
      });
    } else {
      payload[`${args[0]?.[0]}${`${i}`}`] = d;
    }
    duplicated.push(
      `${args[0]?.[0]}${`${i}`}: ${methodLines[0]?.replace(
        match[1] ?? '',
        args.map((type) => `${type[0]}: ${type[1]}${`${i}`}`).join(', ')
      )}`
    );
    duplicated.push(...methodLines.slice(1));
  });
  return {
    queries: duplicated,
    payload
  };
}

/*
 * Extract arguments from strings like
 * "mutation UpdateCategory($expenseCategory: ExpenseCategoryUpdateModelInput!, $uid: String!) {" or
 * "updateExpenseCategory(expenseCategory: $expenseCategory, uid: $uid) {"
 * returns them in array like: [["$expenseCategory", "ExpenseCategoryUpdateModelInput!"], ["$uid", "String!"]]
 * Additionally returns also string that needs to be replaced by string with aliased arguments
 * like: "$expenseCategory: ExpenseCategoryUpdateModelInput!, $uid: String!"
 * */
type ArgName = string;
type ArgType = string;

function getArguments(line: string): { match: RegExpMatchArray; args: [ArgName, ArgType][] } {
  const regex = /\((.*)\)/i;
  const match = line.match(regex);
  const dataType = match?.[1]?.split(/[:,]/).map((c: string) => c.trim());
  if (!dataType || dataType.length % 2 !== 0 || !match || !match?.[1]) {
    throw new Error('Query Parse error');
  }
  const perChunk = 2;

  const result = dataType.reduce<[string, string][]>((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / perChunk);

    if (!resultArray[chunkIndex]) {
      // eslint-disable-next-line no-param-reassign
      resultArray[chunkIndex] = [] as unknown as [string, string];
    }
    resultArray[chunkIndex]?.push(item);
    return resultArray;
  }, []);

  return {
    match,
    args: result
  };
}

function replaceFragments(queryString: string): string {
  const operation = queryString.includes('mutation') ? 'mutation' : 'query';
  const operationStart = queryString.indexOf(operation);
  const fragmentStart = queryString.indexOf('fragment');
  const fragmentStartsAfterOperation = fragmentStart > operationStart;

  const fragmentsContent = fragmentStartsAfterOperation
    ? queryString.slice(fragmentStart)
    : queryString.slice(0, operationStart);

  const fragments = fragmentsContent
    .split('fragment')
    .map((line) => (line.includes('on') ? `fragment${line}` : ''))
    .filter((line) => !!line)
    .map((line) => {
      const contentStart = line.indexOf('{');
      const contentEnd = line.lastIndexOf('}');
      return {
        name: line.split(' ')[1] ?? 'UNKNOWN',
        content: line.slice(contentStart + 1, contentEnd)
      };
    });
  const result = queryString
    .slice(operationStart, fragmentStartsAfterOperation ? fragmentStart : undefined)
    .replaceAll(/\.\.\.\w*/g, (fragmentName) => {
      const name = fragmentName.replace('...', '');
      const content = fragments.find((fragment) => fragment.name === name);
      if (!content) {
        throw new Error(`Fragment "${name} not found in query"`);
      }
      return content.content;
    });
  return result;
}

export function bulkApolloMutationFactory<TData, TVariables>(
  documentNode: TypedDocumentNode<TData, TVariables> | DocumentNode,
  data: TVariables[]
): BulkMutationFactoryOutput<TVariables> {
  const mutationDefinition = documentNode.definitions.find(
    (it) => it.kind === 'OperationDefinition'
  ) as OperationDefinitionNode;
  const mutationName = mutationDefinition?.name?.value;
  if (!mutationDefinition || !mutationName) {
    throw new Error('OperationDefinition not found');
  }
  const otherDefinitions = documentNode.definitions.filter(
    (definition) => !(definition.kind === 'OperationDefinition' && definition.name?.value === mutationName)
  );

  const content = print(mutationDefinition).split('\n');
  const operationLines = content.slice(1, content.length - 1);
  const aliases = createAliasesAndPayload(operationLines, data);

  content[0] = replaceArguments(content[0] ?? '', data);
  content.splice(1, content.length - 2, ...aliases.queries);
  const documentString = `
    ${content.join('\n')}
    ${otherDefinitions.map((it) => `${print(it)}`).join('\n')}
  `;

  return {
    query: documentString,
    apolloQuery: gql(documentString),
    payload: aliases.payload
  };
}
